import { createSelector } from 'reselect'

import { getIsCurrentUserOwner, getIsCurrentUserReviewer } from '@tabeeb/modules/../users/selectors'
import { getFileUploads } from '@tabeeb/modules/fileUploads/selectors'
import {
  getIsChatEnabled,
  getIsCheckInRecordsEnabled,
  getQualificationsEnabled,
} from '@tabeeb/modules/appConfigState/selectors'
import {
  getIsContentLinkingEnabled,
  getIsSelectedPagesForMergingOpen,
  getLinkedContentsList,
} from '@tabeeb/modules/contentSharings/selectors'
import { hasSessionPermission } from '@tabeeb/modules/permissions/selectors'
import { SessionPermission } from '@tabeeb/contracts/main-api/swagger-main-api'
import { getSourceContentReviews } from '@tabeeb/modules/sessionsPage/selectors/contentSharings'
import { RootState } from '@tabeeb/store'
import { TabPanel } from '@tabeeb/enums'

export const getSelectedPanel = (state: RootState, side: 'left' | 'right' | 'bottom') => state.navbar[side]

export const getNavbarCurrentBottomPanel = (state: RootState) => state.navbar.bottom

export const showCustomVideoTab = (state: RootState) => state.customVideo.isTimelineOpen
export const showSpatialViewTab = (state: RootState) => state.spatialModel.isTimelineOpen
export const showThreeDimensionalModelTab = (state: RootState) => state.threedeeModel.isTimelineOpen
export const showShareSessionTab = (state: RootState) => state.shareSessionPagesList.isTimelineOpen
export const showReviewSessionTab = (state: RootState) => state.reviewSessionPagesList.isTimelineOpen
export const showBentleyModelTab = (state: RootState) => state.bentleyModel.isTimelineOpen
export const showBentleyAnalyticsReportTab = (state: RootState) => state.bentleyAnalyticsReport.isTimelineOpen

export const getHasManageReviewsPermission = (state: RootState) =>
  hasSessionPermission(state, SessionPermission.ManageContentReviews)

export const getIsTabOpen = (state: RootState, tabName: TabPanel) => {
  return Object.values(state.navbar).find((panel) => panel?.name === tabName) !== undefined
}

export const getIsTabExpanded = (state: RootState, tabName: TabPanel) => {
  const tab = Object.values(state.navbar).find((panel) => panel?.name === tabName)
  if (!tab) {
    return false
  }
  return tab.expanded
}

export const getIsAnyTabExpanded = (state: RootState) => {
  return Object.values(state.navbar).find((panel) => panel?.expanded) !== undefined
}

export const getIsSessionToolbarFullwidth = (state: RootState) => {
  return state.navbar.left?.expanded ?? false
}

export const showFileUploadsTab = createSelector([getFileUploads], (fileUploads) => {
  return fileUploads.length > 0
})

export const showChatTab = createSelector(
  [getIsCurrentUserReviewer, getIsChatEnabled],
  (isCurrentUserReviewer, isChatEnabled) => {
    return isChatEnabled && !isCurrentUserReviewer
  }
)

export const showActivitiesTab = createSelector([getIsCurrentUserReviewer], (isCurrentUserReviewer) => {
  return !isCurrentUserReviewer
})

export const showLinkedContentsTab = createSelector(
  [getIsContentLinkingEnabled, getLinkedContentsList],
  (isContentsLinkingEnabled, linkedContents) => {
    return isContentsLinkingEnabled && linkedContents.length > 0
  }
)

export const showSelectedPagesForMergingTab = createSelector([getIsSelectedPagesForMergingOpen], (isOpen) => {
  return isOpen
})

export const showCheckInTab = createSelector(
  [getIsCheckInRecordsEnabled, getIsCurrentUserOwner, getIsCurrentUserReviewer],
  (checkInRecordsEnabled, isOwner, isReviewer) => {
    return checkInRecordsEnabled && (isOwner || isReviewer)
  }
)

export const hasEditCheckInsPermission = createSelector(
  [getQualificationsEnabled, getIsCurrentUserOwner],
  (qualificationsEnabled, isOwner) => {
    return qualificationsEnabled && isOwner
  }
)

export const showContentReviewsTab = createSelector(
  [getHasManageReviewsPermission, getSourceContentReviews],
  (hasPermission, reviews) => {
    return hasPermission && reviews.length > 0
  }
)
